import React from 'react';
import '../legal/legal.css';
import { BrandIcon } from '../legal/illustrations';

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight placeholder pages for the header/footer nav tabs that don't have a
// full web experience yet (Events, Profil, Tickets). Each renders the shared
// header + footer and nudges the visitor into the native app (deep link with an
// app-store fallback). Replace the body with the real web feature when ready.
// ─────────────────────────────────────────────────────────────────────────────

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.seuytel.app';
const APP_STORE_URL = 'https://apps.apple.com/app/seuytel/id123456789';

const UA = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
const IS_IOS = /iPhone|iPad|iPod/i.test(UA);

type Variant = 'events' | 'profile' | 'tickets';

const COPY: Record<Variant, { eyebrow: string; title: string; intro: string; body: string; scheme: string }> = {
    events: {
        eyebrow: 'Events',
        title: 'Events entdecken',
        intro: 'Finde Veranstaltungen in deiner Nähe und sichere dir Tickets – in Sekunden.',
        body: 'Die vollständige Event-Suche ist in der MainEvents-App verfügbar. Öffne die App, um zu stöbern, zu filtern und zu buchen.',
        scheme: 'mainevent://events',
    },
    profile: {
        eyebrow: 'Profil',
        title: 'Dein Profil',
        intro: 'Verwalte dein Konto, deine Veranstaltungen und deine Einstellungen.',
        body: 'Dein Profil und deine Kontoeinstellungen verwaltest du am besten direkt in der MainEvents-App.',
        scheme: 'mainevent://profile',
    },
    tickets: {
        eyebrow: 'Tickets',
        title: 'Deine Tickets',
        intro: 'Alle gekauften Tickets – jederzeit griffbereit für den Einlass.',
        body: 'Deine Tickets liegen sicher in der MainEvents-App und sind dort offline und für den Einlass verfügbar.',
        scheme: 'mainevent://tickets',
    },
};

export default function AppLandingPage({ variant }: { variant: Variant }) {
    const c = COPY[variant];

    const openInApp = () => {
        try {
            window.location.href = c.scheme;
        } catch {
            /* unknown scheme — the store button covers the fallback */
        }
    };
    const goToStore = () => {
        window.location.href = IS_IOS ? APP_STORE_URL : PLAY_STORE_URL;
    };

    return (
        <>
            <section className="se-hero">
                <div className="se-hero-inner">
                    <div className="se-hero-text">
                        <span className="se-eyebrow">{c.eyebrow}</span>
                        <h1>{c.title}</h1>
                        <p>{c.intro}</p>
                    </div>
                    <div className="se-hero-art">
                        <BrandIcon />
                    </div>
                </div>
            </section>

            <main className="se-content">
                <p>{c.body}</p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
                    <button type="button" className="se-reopen" onClick={openInApp}>
                        In der App öffnen
                    </button>
                    <button type="button" className="se-reopen" onClick={goToStore} style={{ background: 'var(--se-blue)' }}>
                        {IS_IOS ? 'Im App Store laden' : 'Bei Google Play laden'}
                    </button>
                </div>
            </main>
        </>
    );
}
